import { useEffect, useMemo, useRef, useState } from "react";
import { useUser } from "@clerk/react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListComments,
  useCreateComment,
  useUpdateComment,
  useDeleteComment,
  getListCommentsQueryKey,
  getListMyMentionsQueryKey,
} from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { MessageSquare, Send, Pencil, Trash2, X, Check, Lock } from "lucide-react";
import { useFeature } from "@/lib/useFeature";
import { Link } from "wouter";

interface TeamMember {
  id: number;
  email: string;
  name: string | null;
  status: "active" | "pending";
}

interface MentionDraft {
  memberId: number;
  name: string;
  email: string;
}

function useTeamMembers() {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancel = false;
    fetch("/api/team/members")
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        if (cancel) return;
        setMembers(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, []);
  return { members, loading };
}

function renderBody(body: string, mentions: { name: string }[]) {
  const names = new Set(mentions.map((m) => m.name.toLowerCase()));
  const parts: Array<{ kind: "text" | "mention"; value: string }> = [];
  const re = /@([A-Za-z0-9._-]+(?:\s+[A-Za-z0-9._-]+){0,2})/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(body)) !== null) {
    if (m.index > last) parts.push({ kind: "text", value: body.slice(last, m.index) });
    const candidate = m[1];
    if (names.has(candidate.toLowerCase())) {
      parts.push({ kind: "mention", value: `@${candidate}` });
    } else {
      parts.push({ kind: "text", value: m[0] });
    }
    last = m.index + m[0].length;
  }
  if (last < body.length) parts.push({ kind: "text", value: body.slice(last) });

  return parts.map((p, i) =>
    p.kind === "mention" ? (
      <span key={i} className="bg-blue-100 text-blue-800 rounded px-1 py-0.5 text-xs font-medium">
        {p.value}
      </span>
    ) : (
      <span key={i}>{p.value}</span>
    ),
  );
}

interface ComposerProps {
  members: TeamMember[];
  initialBody?: string;
  initialMentions?: MentionDraft[];
  submitLabel: string;
  onSubmit: (body: string, mentionedMemberIds: number[]) => void;
  onCancel?: () => void;
  pending?: boolean;
}

function Composer({ members, initialBody = "", initialMentions = [], submitLabel, onSubmit, onCancel, pending }: ComposerProps) {
  const [body, setBody] = useState(initialBody);
  const [mentions, setMentions] = useState<MentionDraft[]>(initialMentions);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const tokenStart = useRef<number>(-1);
  const mentionsFeat = useFeature("comments.mentions");

  const memberLabel = (m: TeamMember) => m.name?.trim() || m.email;

  const filtered = useMemo(() => {
    const q = pickerQuery.toLowerCase();
    return members
      .filter((m) => m.status === "active")
      .filter((m) => !q || memberLabel(m).toLowerCase().includes(q) || m.email.toLowerCase().includes(q))
      .slice(0, 6);
  }, [members, pickerQuery]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setBody(value);
    const cursor = e.target.selectionStart;
    const before = value.slice(0, cursor);
    const atIdx = before.lastIndexOf("@");
    if (atIdx >= 0) {
      const between = before.slice(atIdx + 1);
      const validBefore = atIdx === 0 || /\s/.test(before[atIdx - 1]);
      if (validBefore && !/\s/.test(between)) {
        tokenStart.current = atIdx;
        setPickerQuery(between);
        setPickerOpen(true);
        setActiveIdx(0);
        return;
      }
    }
    setPickerOpen(false);
    tokenStart.current = -1;
  };

  const insertMention = (m: TeamMember) => {
    if (tokenStart.current < 0) return;
    const label = memberLabel(m);
    const before = body.slice(0, tokenStart.current);
    const cursor = textareaRef.current?.selectionStart ?? body.length;
    const after = body.slice(cursor);
    const newBody = `${before}@${label} ${after}`;
    setBody(newBody);
    setMentions((cur) => (cur.find((x) => x.memberId === m.id) ? cur : [...cur, { memberId: m.id, name: label, email: m.email }]));
    setPickerOpen(false);
    tokenStart.current = -1;
    setTimeout(() => {
      const pos = before.length + 2 + label.length;
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(pos, pos);
    }, 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (pickerOpen && filtered.length > 0) {
      if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => (i + 1) % filtered.length); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => (i - 1 + filtered.length) % filtered.length); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); insertMention(filtered[activeIdx]); return; }
      if (e.key === "Escape") { e.preventDefault(); setPickerOpen(false); return; }
    }
    if ((e.key === "Enter" && (e.metaKey || e.ctrlKey))) {
      e.preventDefault();
      submit();
    }
  };

  const submit = () => {
    if (!body.trim()) return;
    const used = mentions.filter((m) => body.includes(`@${m.name}`));
    onSubmit(body.trim(), used.map((m) => m.memberId));
  };

  return (
    <div className="space-y-2">
      <div className="relative">
        <Textarea
          ref={textareaRef}
          value={body}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={mentionsFeat.enabled
            ? "Write a comment… use @ to mention a teammate"
            : "Write a comment…"}
          className="min-h-[80px]"
        />
        {pickerOpen && mentionsFeat.enabled && filtered.length > 0 && (
          <div className="absolute left-2 right-2 mt-1 z-20 bg-popover border rounded-md shadow-md max-h-56 overflow-auto">
            {filtered.map((m, i) => (
              <button
                key={m.id}
                type="button"
                className={`w-full text-left px-3 py-2 text-sm flex flex-col gap-0.5 ${i === activeIdx ? "bg-muted" : "hover:bg-muted/50"}`}
                onMouseDown={(e) => { e.preventDefault(); insertMention(m); }}
                onMouseEnter={() => setActiveIdx(i)}
              >
                <span className="font-medium">{memberLabel(m)}</span>
                <span className="text-xs text-muted-foreground">{m.email}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {!mentionsFeat.enabled && (
        <div className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-800">
          <Lock className="h-3 w-3" />
          <span>Team @-mentions are a Starter feature</span>
          <Link href="/settings" className="underline hover:no-underline">Upgrade</Link>
        </div>
      )}
      <div className="flex items-center justify-between">
        <div className="flex flex-wrap gap-1">
          {mentionsFeat.enabled && mentions.filter((m) => body.includes(`@${m.name}`)).map((m) => (
            <Badge key={m.memberId} variant="secondary" className="text-xs">@{m.name}</Badge>
          ))}
        </div>
        <div className="flex gap-2">
          {onCancel && (
            <Button variant="ghost" size="sm" onClick={onCancel} disabled={pending}>
              <X className="h-4 w-4 mr-1" /> Cancel
            </Button>
          )}
          <Button size="sm" onClick={submit} disabled={!body.trim() || pending}>
            <Send className="h-4 w-4 mr-2" />
            {pending ? "Saving…" : submitLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

interface CommentsSectionProps {
  entityType: "contract" | "coach";
  entityId: number;
}

export function CommentsSection({ entityType, entityId }: CommentsSectionProps) {
  const { toast } = useToast();
  const { user } = useUser();
  const queryClient = useQueryClient();
  const { members } = useTeamMembers();

  const queryParams = { entityType, entityId };
  const queryKey = getListCommentsQueryKey(queryParams);

  const { data: comments, isLoading } = useListComments(queryParams, { query: { queryKey } });
  const createMut = useCreateComment();
  const updateMut = useUpdateComment();
  const deleteMut = useDeleteComment();
  const [editingId, setEditingId] = useState<number | null>(null);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey });
    queryClient.invalidateQueries({ queryKey: getListMyMentionsQueryKey() });
  };

  const handleCreate = (body: string, mentionedMemberIds: number[]) => {
    createMut.mutate(
      { data: { entityType, entityId, body, mentionedMemberIds } },
      {
        onSuccess: () => {
          toast({ title: "Comment added" });
          invalidate();
        },
        onError: () => toast({ title: "Error", description: "Failed to add comment.", variant: "destructive" }),
      },
    );
  };

  const handleUpdate = (id: number, body: string, mentionedMemberIds: number[]) => {
    updateMut.mutate(
      { id, data: { body, mentionedMemberIds } },
      {
        onSuccess: () => {
          toast({ title: "Comment updated" });
          setEditingId(null);
          invalidate();
        },
        onError: () => toast({ title: "Error", description: "Failed to update.", variant: "destructive" }),
      },
    );
  };

  const handleDelete = (id: number) => {
    if (!confirm("Delete this comment?")) return;
    deleteMut.mutate(
      { id },
      {
        onSuccess: () => { toast({ title: "Comment deleted" }); invalidate(); },
        onError: () => toast({ title: "Error", description: "Failed to delete.", variant: "destructive" }),
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-muted-foreground" />
          Comments {comments ? <span className="text-sm text-muted-foreground font-normal">({comments.length})</span> : null}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {user && (
          <Composer
            members={members}
            submitLabel="Post comment"
            onSubmit={handleCreate}
            pending={createMut.isPending}
          />
        )}

        {isLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : !comments || comments.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-6">
            No comments yet. Start the conversation.
          </div>
        ) : (
          <div className="space-y-3">
            {comments.map((c) => (
              <div key={c.id} className="border rounded-md p-3 bg-muted/20">
                <div className="flex items-start justify-between mb-2 gap-2">
                  <div>
                    <div className="text-sm font-medium">{c.authorName}</div>
                    <div className="text-xs text-muted-foreground">
                      {format(new Date(c.createdAt), "MMM d, yyyy h:mm a")}
                      {c.edited && <span className="ml-1 italic">(edited)</span>}
                    </div>
                  </div>
                  {c.canEdit && editingId !== c.id && (
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditingId(c.id)} aria-label="Edit comment">
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDelete(c.id)} aria-label="Delete comment">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
                {editingId === c.id ? (
                  <Composer
                    members={members}
                    initialBody={c.body}
                    initialMentions={c.mentions.map((m) => ({ memberId: m.memberId, name: m.name, email: m.email }))}
                    submitLabel="Save"
                    pending={updateMut.isPending}
                    onSubmit={(body, ids) => handleUpdate(c.id, body, ids)}
                    onCancel={() => setEditingId(null)}
                  />
                ) : (
                  <div className="text-sm whitespace-pre-wrap leading-relaxed">{renderBody(c.body, c.mentions)}</div>
                )}
                {c.mentions.length > 0 && editingId !== c.id && (
                  <div className="mt-2 flex items-center gap-1 flex-wrap">
                    <Check className="h-3 w-3 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Notified:</span>
                    {c.mentions.map((m) => (
                      <Badge key={m.memberId} variant="outline" className="text-xs">{m.name}</Badge>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
